import { IInvoice, IInvoicesPage } from '../interfaces/heavyDutyMachinery/IInvoice';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** Whatever the caller is entitled to see for this booking -- 0, 1 (guest) or 2 (host/admin) items, never a 404. */
export const getInvoicesForBooking = async (bookingId: string): Promise<IInvoice[]> => {
  const response = await apiService<IInvoice[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_INVOICES(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load invoices');
};

export const getAdminInvoices = async (
  type?: string,
  search?: string,
  before?: string
): Promise<IInvoicesPage> => {
  const params: Record<string, string> = {};
  if (type) params.type = type;
  if (search) params.search = search;
  if (before) params.before = before;
  const response = await apiService<IInvoicesPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_INVOICES,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load invoices');
};

/** Streams the raw PDF (not the usual ApiResponse<T> envelope), same download-via-blob pattern as downloadMyDataExport. */
const downloadInvoiceFrom = async (url: string, invoiceNumber: string): Promise<void> => {
  const accessToken = getLocalStorageKey('accessToken');
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (!response.ok) throw new Error(`Failed to download invoice (HTTP ${response.status})`);

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const downloadInvoice = (invoice: IInvoice): Promise<void> =>
  downloadInvoiceFrom(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD(invoice.id), invoice.invoiceNumber);

export const downloadAdminInvoice = (invoice: IInvoice): Promise<void> =>
  downloadInvoiceFrom(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_INVOICE_DOWNLOAD(invoice.id), invoice.invoiceNumber);

/** RD 1619/2012 Art. 7.2 self-service: turns a Simplificada into a Simplificada Cualificada by adding the buyer's own NIF/address -- same InvoiceNumber, PDF regenerated server-side. */
export const addInvoiceTaxInfo = async (invoiceId: string, taxId: string, address: string): Promise<IInvoice> => {
  const response = await apiService<IInvoice>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INVOICE_TAX_INFO(invoiceId),
    method: HTTP_METHODS.POST,
    body: { taxId, address },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to add tax info');
};
